/**
 * The rules of akari, ported from `backend/apps/puzzletypes/akari/rules.py` (parity through the
 * shared fixtures), plus the live contradictions, which only the client computes.
 *
 * A grid is a list of row strings. Nothing here ever sees the answer key.
 */
import { BLACK, BULB, CROSS, EMPTY, NUMBERS, WHITE, type Cell } from "./types";

const ORTHOGONAL: Cell[] = [
  [-1, 0],
  [1, 0],
  [0, -1],
  [0, 1],
];

export const key = (r: number, c: number): string => `${r},${c}`;

export function isWhite(grid: string[], r: number, c: number): boolean {
  return grid[r]?.[c] === WHITE;
}

export function isNumber(char: string | undefined): boolean {
  return char !== undefined && char.length === 1 && NUMBERS.includes(char);
}

export function cellsOf(rows: string[], char: string): Cell[] {
  const found: Cell[] = [];
  rows.forEach((row, r) => {
    for (let c = 0; c < row.length; c += 1) if (row[c] === char) found.push([r, c]);
  });
  return found;
}

export function whiteNeighbours(grid: string[], r: number, c: number): Cell[] {
  return ORTHOGONAL.map(([dr, dc]): Cell => [r + dr, c + dc]).filter(([rr, cc]) =>
    isWhite(grid, rr, cc),
  );
}

/** Every white cell a bulb on (r, c) lights, itself excepted, each way until a black cell. */
export function sight(grid: string[], r: number, c: number): Cell[] {
  const seen: Cell[] = [];
  for (const [dr, dc] of ORTHOGONAL) {
    let rr = r + dr;
    let cc = c + dc;
    while (isWhite(grid, rr, cc)) {
      seen.push([rr, cc]);
      rr += dr;
      cc += dc;
    }
  }
  return seen;
}

/** The bulbs of a mark grid that stand on white cells. */
export function bulbsOn(grid: string[], marks: string[]): Cell[] {
  return cellsOf(marks, BULB).filter(([r, c]) => isWhite(grid, r, c));
}

/** Every white cell a bulb lights, the bulbs' own cells included, as `r,c` keys. */
export function litCells(grid: string[], bulbs: Cell[]): Set<string> {
  const lit = new Set<string>();
  for (const [r, c] of bulbs) {
    if (!isWhite(grid, r, c)) continue;
    lit.add(key(r, c));
    for (const [rr, cc] of sight(grid, r, c)) lit.add(key(rr, cc));
  }
  return lit;
}

/** Every bulb that another bulb lights, as `r,c` keys. */
export function seeing(grid: string[], bulbs: Cell[]): Set<string> {
  const all = new Set(bulbs.map(([r, c]) => key(r, c)));
  return new Set(
    bulbs
      .filter(([r, c]) => sight(grid, r, c).some(([rr, cc]) => all.has(key(rr, cc))))
      .map(([r, c]) => key(r, c)),
  );
}

function numberedCells(grid: string[]): { cell: Cell; n: number }[] {
  const found: { cell: Cell; n: number }[] = [];
  grid.forEach((row, r) => {
    for (let c = 0; c < row.length; c += 1)
      if (isNumber(row[c])) found.push({ cell: [r, c], n: Number(row[c]) });
  });
  return found;
}

/** Every rule the bulbs break, as the server's codes; empty when the answer is right. */
export function issues(grid: string[], bulbRows: string[]): string[] {
  const all = cellsOf(bulbRows, BULB);
  const bulbs = all.filter(([r, c]) => isWhite(grid, r, c));
  const bulbKeys = new Set(bulbs.map(([r, c]) => key(r, c)));
  const found: string[] = [];
  if (bulbs.length !== all.length) found.push("bulb_on_black");
  if (seeing(grid, bulbs).size > 0) found.push("bulbs_see_each_other");
  const lit = litCells(grid, bulbs);
  if (cellsOf(grid, WHITE).some(([r, c]) => !lit.has(key(r, c)))) found.push("cells_dark");
  const mismatch = numberedCells(grid).some(
    ({ cell: [r, c], n }) =>
      whiteNeighbours(grid, r, c).filter(([rr, cc]) => bulbKeys.has(key(rr, cc))).length !== n,
  );
  if (mismatch) found.push("number_mismatch");
  return found;
}

export interface Contradictions {
  /** Bulbs lit by another bulb. */
  bulbs: Set<string>;
  /** White cells that are dark and that no bulb can light any more. */
  dark: Set<string>;
  /** Numbers with too many bulbs beside them, or too few cells left to reach them. */
  numbers: Set<string>;
}

/**
 * What the board can no longer be, from the board and the marks (`*`, `x`, `.`) alone. Sound,
 * not complete: every flag is a real mistake, and whatever needs reasoning beyond one bulb,
 * one cell or one number stays silent (CLAUDE.md §16.4). A cell can still take a bulb when it
 * is undecided and dark: a lit cell would put its bulb in another bulb's light.
 */
export function contradictions(grid: string[], marks: string[]): Contradictions {
  const bulbs = bulbsOn(grid, marks);
  const bulbKeys = new Set(bulbs.map(([r, c]) => key(r, c)));
  const lit = litCells(grid, bulbs);
  const candidate = ([r, c]: Cell) =>
    isWhite(grid, r, c) && (marks[r]?.[c] ?? EMPTY) === EMPTY && !lit.has(key(r, c));

  const numbers = new Set<string>();
  for (const {
    cell: [r, c],
    n,
  } of numberedCells(grid)) {
    const around = whiteNeighbours(grid, r, c);
    const placed = around.filter(([rr, cc]) => bulbKeys.has(key(rr, cc))).length;
    const open = around.filter(candidate).length;
    if (placed > n || placed + open < n) numbers.add(key(r, c));
  }

  const dark = new Set<string>();
  for (const [r, c] of cellsOf(grid, WHITE)) {
    if (lit.has(key(r, c))) continue;
    if (![[r, c] as Cell, ...sight(grid, r, c)].some(candidate)) dark.add(key(r, c));
  }
  return { bulbs: seeing(grid, bulbs), dark, numbers };
}

/** Numbers that are met and have no undecided dark neighbour left: they step back. */
export function doneNumbers(grid: string[], marks: string[]): Set<string> {
  const bulbs = bulbsOn(grid, marks);
  const bulbKeys = new Set(bulbs.map(([r, c]) => key(r, c)));
  const lit = litCells(grid, bulbs);
  const done = new Set<string>();
  for (const {
    cell: [r, c],
    n,
  } of numberedCells(grid)) {
    const around = whiteNeighbours(grid, r, c);
    const placed = around.filter(([rr, cc]) => bulbKeys.has(key(rr, cc))).length;
    const open = around.some(
      ([rr, cc]) =>
        !bulbKeys.has(key(rr, cc)) && (marks[rr]?.[cc] ?? EMPTY) !== CROSS && !lit.has(key(rr, cc)),
    );
    if (placed === n && !open) done.add(key(r, c));
  }
  return done;
}

/** The board with every black cell numbered from the bulbs, for the author. */
export function numberedFrom(grid: string[], bulbRows: string[]): string[] {
  const bulbKeys = new Set(bulbsOn(grid, bulbRows).map(([r, c]) => key(r, c)));
  return grid.map((row, r) =>
    Array.from(row, (char, c) =>
      char === WHITE
        ? WHITE
        : String(
            whiteNeighbours(grid, r, c).filter(([rr, cc]) => bulbKeys.has(key(rr, cc))).length,
          ),
    ).join(""),
  );
}

/** The board with every number wiped, the black cells kept. */
export function unnumbered(grid: string[]): string[] {
  return grid.map((row) => Array.from(row, (char) => (char === WHITE ? WHITE : BLACK)).join(""));
}
